/**
 * 코드값 표시 문자열.
 *
 * 값의 출처는 서버 `app/core/enums.py` 하나뿐이고, `GET /api/v1/codes` 로도
 * 내려온다. 여기에는 **표시용 한글 라벨만** 둔다 — 레거시는 코드값 자체를
 * PHP(collections.php) / JSP / Java / Dart 에 네 벌 복사해 두어서 한쪽만
 * 바뀌면 화면마다 다른 상태명이 보였다.
 */

export const ORDER_STATE_LABEL: Record<number, string> = {
  0: '온라인접수',
  1: '주문접수',
  2: '기사배정',
  3: '계약대기',
  4: '계약완료',
  5: '취소',
};

export const AS_STATE_LABEL: Record<number, string> = {
  1: '미배정',
  2: '배정-미처리',
  3: '처리완료',
};

export const APT_TYPE_LABEL: Record<number, string> = {
  1: '일반아파트',
  2: 'SH 임대',
  3: '다세대(빌라)',
  4: '다가구',
  5: '단독주택',
  6: '경비실',
  7: 'SH 재개발',
};

export const INSTALL_COMPLETE_LABEL: Record<string, string> = {
  N: '미설치',
  Y: '설치완료',
  C: '취소',
};

export const INSTALL_TYPE_LABEL: Record<string, string> = {
  hook: '수평 앵커형',
  attachment: '거치형',
  slope_hook: '경사 앵커형',
};

export const GU_STATE_LABEL: Record<number, string> = {
  1: '배정대기',
  2: '지급(시)',
  3: '지급(구)',
  4: '미배정',
};

export const DOC_STATE_LABEL: Record<number, string> = {
  1: '미제출',
  2: '제출',
  3: '접수',
  4: '보류',
};

export const ROLE_LABEL: Record<number, string> = {
  1: '에너지공사 관리자',
  2: '에너지공사 직원',
  3: '보급업체 관리자',
  4: '설치기사',
  5: '보급업체 직원',
  6: '시 관리자',
  7: '구 관리자',
  8: '영업사원',
  9: '콜센터',
};

const WON = new Intl.NumberFormat('ko-KR');

export function won(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${WON.format(value)}원`;
}

/**
 * 레거시 DB 는 날짜를 `DATE`, `DATETIME`, `VARCHAR('20240101')` 세 형태로 섞어
 * 저장한다. 어느 쪽이 와도 깨지지 않게 처리하고, 못 읽으면 원문을 그대로 보여준다.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ko-KR');
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
}

export function formatPhone(value: string | null | undefined): string {
  if (!value) return '-';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  if (digits.length === 10) return digits.replace(/(\d{2,3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  return value;
}

/**
 * 주문 주소 한 줄 — addr_full 뒤에 건물명·동/호수를 붙이되, 이미 addr_full 에 들어 있으면
 * 생략한다. 시민 신청은 서버가 addr_full 을 도로명+건물명+동+호로 합성하고(citizen_service),
 * 레거시 주소검색 입력은 도로명만 addr_full 에 두므로 두 경우 모두 한 번씩만 나온다.
 */
export function displayAddress(
  addrFull: string | null | undefined,
  ...details: Array<string | null | undefined>
): string {
  const base = (addrFull ?? '').trim();
  const parts = [base];
  for (const d of details) {
    const text = (d ?? '').trim();
    if (text && !base.includes(text)) parts.push(text);
  }
  return parts.filter(Boolean).join(' ') || '-';
}
